import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  Sparkles, 
  Clock, 
  FileText, 
  Download, 
  Eye, 
  Phone, 
  Mail, 
  Briefcase, 
  ShieldAlert, 
  Brain, 
  Compass, 
  Target, 
  HeartHandshake, 
  MessagesSquare, 
  Building2, 
  Scale, 
  TrendingUp, 
  Info,
  Maximize2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PdfResourceCard from '../components/PdfResourceCard';
import PdfModal from '../components/PdfModal';
import { imageUrl } from '../lib/images';
import { siteConfig } from '../data';

const TrivselIFaellesskabet = () => {
  const [activePdfModal, setActivePdfModal] = useState(null);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  const pdfDocs = [
    {
      id: 'trivsel-faellesskab',
      title: 'Trivsel i fællesskabet',
      subtitle: 'Forebyggelse, håndtering & styrkelse',
      description: 'Komplet oplæg over TRIVlogi® i arbejdsfællesskabet: Før, under og efter stress, de tre niveauer og indsatser fra den enkelte til hele teamet.',
      url: '/_assets/trivsel-i-faelleskabet.pdf',
      tag: 'Oversigt & Guide (2 sider)',
      highlight: true,
      badgeText: 'Til virksomheder'
    },
    {
      id: 'virksomhedspriser',
      title: 'Priser til virksomheder',
      subtitle: 'Foredrag, workshops & individuel støtte',
      description: 'Gennemskuelig prisoversigt over foredrag (9.500 kr.), workshops (14.500 kr.), temadage (22.500 kr.), 1:1 samtaler og løbende aftaler (ekskl. moms).',
      url: '/_assets/priser-v2.pdf',
      tag: 'Erhvervspriser'
    }
  ];

  return (
    <div className="bg-white text-zinc-900">
      {/* Hero Section */}
      <section className="relative bg-emerald-950 text-white overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 -z-10 opacity-20">
          <img 
            src="/materiale-260428/3-Kasse_alle_2.png" 
            alt="Trivsel i fællesskabet baggrund" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/90 via-emerald-950/95 to-emerald-950 -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-800/80 border border-emerald-700/60 text-emerald-200 text-xs sm:text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              TRIVlogi® · Biologien bag trivsel
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
              TRIVSEL I FÆLLESSKABET
            </h1>
            <p className="text-xl sm:text-2xl text-emerald-100 font-medium mb-6 leading-snug">
              Forebyggelse af stress · hjælp når stressen rammer · stærkere arbejdsfællesskaber
            </p>
            <div className="space-y-4 text-base sm:text-lg text-emerald-200/90 leading-relaxed max-w-2xl mb-10">
              <p className="font-semibold text-white">
                Stress starter sjældent den dag, medarbejderen sygemelder sig.
              </p>
              <p>
                Derfor arbejder jeg både før, under og efter stress. Med biologien som udgangspunkt skaber vi forståelse for kroppens reaktioner, arbejder med mindset og omsætter det til konkrete strategier, der kan bruges i hverdagen.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 px-8 py-4 font-bold text-base sm:text-lg rounded-xl transition-all shadow-lg flex items-center gap-2 group"
              >
                Tag en uforpligtende snak
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#priser"
                className="bg-emerald-900/80 hover:bg-emerald-800 text-white border border-emerald-700 px-6 py-4 font-semibold text-base rounded-xl transition-all flex items-center gap-2"
              >
                Se erhvervspriser
              </a>
              <a
                href="#materiale-pdf"
                className="bg-emerald-900/40 hover:bg-emerald-800/80 text-emerald-200 hover:text-white border border-emerald-700/60 px-5 py-4 font-medium text-base rounded-xl transition-all flex items-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Hent PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Søjler: Biologi - Mindset - Strategi */}
      <section className="py-16 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-zinc-50 rounded-2xl border border-zinc-200 shadow-sm flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-emerald-900 text-white flex items-center justify-center font-bold text-lg mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-emerald-950 mb-2">BIOLOGI</h3>
              <p className="text-slate-700 leading-relaxed text-base">
                Forstå kroppens og nervesystemets reaktioner.
              </p>
            </div>

            <div className="p-8 bg-zinc-50 rounded-2xl border border-zinc-200 shadow-sm flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-emerald-800 text-white flex items-center justify-center font-bold text-lg mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-emerald-950 mb-2">MINDSET</h3>
              <p className="text-slate-700 leading-relaxed text-base">
                Forstå mønstre, forventninger og reaktioner.
              </p>
            </div>

            <div className="p-8 bg-zinc-50 rounded-2xl border border-zinc-200 shadow-sm flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center font-bold text-lg mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-emerald-950 mb-2">STRATEGI</h3>
              <p className="text-slate-700 leading-relaxed text-base">
                Omsæt forståelsen til konkrete handlinger.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Én indsats – tre niveauer */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-full inline-block mb-3">
              Helhedsorienteret tilgang
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Én indsats – tre niveauer
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              Indsatser der understøtter både forebyggelse, håndtering og det relationelle fællesskab.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-md flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-emerald-600" />
              <div>
                <span className="text-3xl font-black text-emerald-800 block mb-2">01</span>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">FOREBYG</h3>
                <p className="text-emerald-900 font-semibold text-sm mb-4">
                  Før stress bliver til en sygemelding.
                </p>
                <p className="text-slate-700 leading-relaxed text-base">
                  Foredrag, workshops og 1:1-samtaler gør det lettere at opdage belastningssignaler og reagere tidligere.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-md flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-emerald-700" />
              <div>
                <span className="text-3xl font-black text-emerald-800 block mb-2">02</span>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">HÅNDTER</h3>
                <p className="text-emerald-900 font-semibold text-sm mb-4">
                  Når stressen allerede fylder.
                </p>
                <p className="text-slate-700 leading-relaxed text-base">
                  Individuelle forløb med fokus på biologisk ro, reaktionsmønstre og en mere bæredygtig tilbagevenden.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-md flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-emerald-800" />
              <div>
                <span className="text-3xl font-black text-emerald-800 block mb-2">03</span>
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">STYRK</h3>
                <p className="text-emerald-900 font-semibold text-sm mb-4">
                  Trivsel skabes også mellem mennesker.
                </p>
                <p className="text-slate-700 leading-relaxed text-base">
                  Kommunikation, relationer, konflikthåndtering, grænsesætning, ledelse og fælles ansvar.
                </p>
              </div>
            </div>
          </div>

          {/* Erfaring fra arbejdet med stressramte */}
          <div className="bg-white rounded-3xl p-8 sm:p-12 lg:p-14 border border-zinc-200 shadow-lg">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl sm:text-3xl font-bold text-emerald-950 mb-6">
                Min erfaring fra arbejdet med stressramte
              </h3>
              <p className="text-slate-700 text-base sm:text-lg leading-relaxed mb-8">
                Når mennesker forstår hvad der sker biologisk, oplever jeg, at det bliver lettere at skabe ro, genvinde overblik og arbejde konstruktivt med de mønstre og belastninger, der har ført til stress. Min erfaring er, at tilgangen kan understøtte en hurtigere vej tilbage til arbejdslivet og samtidig gøre medarbejderen bedre rustet til at opdage og håndtere belastning fremover.
              </p>

              <div className="p-6 sm:p-8 bg-emerald-950 text-white rounded-2xl border border-emerald-800">
                <p className="text-lg sm:text-xl font-bold leading-relaxed text-emerald-100">
                  "Målet er ikke kun at få medarbejderen tilbage. Målet er at hjælpe medarbejderen tilbage med større forståelse for sig selv og bedre værktøjer til at blive der."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infografik-sektion: Mindre stress, stærkere fællesskaber */}
      <section className="py-20 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 px-3 py-1 rounded-full inline-block">
                Visuel model
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 leading-tight">
                Mindre stress. <br />
                <span className="text-emerald-700">Stærkere fællesskaber.</span>
              </h2>
              <p className="text-lg text-slate-700 leading-relaxed font-medium">
                Når vi forstår, hvad der sker i kroppen under stress, bliver det lettere at forstå os selv – og hinanden.
              </p>
              <div className="space-y-3 pt-2 text-slate-600 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />
                  <span><strong>1. Biologi:</strong> Forstå hvad der sker i kroppen og hjernen</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />
                  <span><strong>2. Mindset:</strong> Forstå dine reaktionsmønstre og muligheder</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />
                  <span><strong>3. Strategi:</strong> Find konkrete værktøjer, der virker i hverdagen</span>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setShowImageLightbox(true)}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-900 hover:bg-zinc-800 text-white font-semibold text-sm rounded-xl transition-colors shadow-sm"
                >
                  <Maximize2 className="w-4 h-4" />
                  Se infografik i stor
                </button>
                <a
                  href="/_assets/mindre-stress-staerkere-faellesskaber.png"
                  download
                  className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-semibold text-sm rounded-xl transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Hent billede
                </a>
              </div>
            </div>

            <div className="lg:col-span-7 flex justify-center">
              <div 
                className="relative bg-zinc-50 p-4 rounded-3xl border border-zinc-200 shadow-xl max-w-lg cursor-pointer group"
                onClick={() => setShowImageLightbox(true)}
              >
                <img
                  src={imageUrl('/_assets/mindre-stress-staerkere-faellesskaber.png', 'width=1200,quality=90,format=auto')}
                  alt="Mindre stress, stærkere fællesskaber - TRIVlogi model"
                  className="w-full h-auto rounded-2xl shadow-sm transition-transform duration-300 group-hover:scale-[1.01]"
                />
                <div className="absolute inset-0 bg-emerald-950/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl flex items-center justify-center">
                  <span className="inline-flex items-center gap-2 bg-white text-zinc-900 px-4 py-2 rounded-xl text-sm font-bold shadow-lg">
                    <Maximize2 className="w-4 h-4" /> Klik for at forstørre
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fra den enkelte til hele arbejdsfællesskabet */}
      <section className="py-24 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-full inline-block mb-3">
              Ydelser &amp; Formater
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Fra den enkelte til hele arbejdsfællesskabet
            </h2>
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
              Stress er ikke kun et individuelt spørgsmål. Derfor arbejder jeg også med det, der sker mellem mennesker – og med de rammer, ledelsen er med til at skabe.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* 1. FOREDRAG */}
            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-900 flex items-center justify-center mb-6">
                  <MessagesSquare className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">FOREDRAG</h3>
                <p className="text-slate-700 text-base leading-relaxed mb-6">
                  Fælles sprog for stress og trivsel. Biologi, tidlige signaler og handlemuligheder.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>Varighed: 60-90 min</span>
                <span className="text-emerald-800 font-bold">9.500 kr.</span>
              </div>
            </div>

            {/* 2. WORKSHOP */}
            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-900 flex items-center justify-center mb-6">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">WORKSHOP</h3>
                <p className="text-slate-700 text-base leading-relaxed mb-6">
                  Aktivt arbejde med egne mønstre, kommunikation, belastninger og fælles strategier.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>Varighed: Ca. 3 timer</span>
                <span className="text-emerald-800 font-bold">14.500 kr.</span>
              </div>
            </div>

            {/* 3. 1:1 SAMTALER */}
            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-900 flex items-center justify-center mb-6">
                  <HeartHandshake className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">1:1 SAMTALER</h3>
                <p className="text-slate-700 text-base leading-relaxed mb-6">
                  Fortrolige samtaler ved pres, mistrivsel, stress, konflikter eller behov for tidlig støtte.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>Enkelt session (60 min)</span>
                <span className="text-emerald-800 font-bold">1.500 kr.</span>
              </div>
            </div>

            {/* 4. STRESSFORLØB */}
            <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-900 flex items-center justify-center mb-6">
                  <ShieldAlert className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">STRESSFORLØB</h3>
                <p className="text-slate-700 text-base leading-relaxed mb-6">
                  Individuel støtte til stressramte eller sygemeldte med fokus på ro, forståelse og bæredygtig tilbagevenden.
                </p>
              </div>
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>6 x 60 min. forløb</span>
                <span className="text-emerald-800 font-bold">8.500 kr.</span>
              </div>
            </div>

            {/* 5. KONFLIKTER (integreret) */}
            <div id="konflikter" className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-900 flex items-center justify-center mb-6">
                  <Scale className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">KONFLIKTER</h3>
                <p className="text-slate-700 text-base leading-relaxed mb-4">
                  Forstå reaktionerne bag konflikten og skab bedre kommunikation, relationer og handlemuligheder.
                </p>
                <div className="space-y-1.5 text-xs text-slate-600 mb-4 bg-zinc-50 p-3 rounded-xl border border-zinc-100">
                  <p>• Afvæbnende dialog &amp; kig bag reaktionerne</p>
                  <p>• Psykologisk tryghed i teams</p>
                  <p>• Værktøjer til at skabe ro i pressede situationer</p>
                </div>
              </div>
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>Indgår i workshops &amp; temadage</span>
                <Link to="/kontakt" className="text-emerald-800 font-bold hover:underline">Hør mere</Link>
              </div>
            </div>

            {/* 6. LEDELSE (integreret) */}
            <div id="ledelse" className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-900 flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">LEDELSE</h3>
                <p className="text-slate-700 text-base leading-relaxed mb-4">
                  Sparring om stress, kommunikation, relationer og lederens rolle i et sundt trivselsfællesskab.
                </p>
                <div className="space-y-1.5 text-xs text-slate-600 mb-4 bg-zinc-50 p-3 rounded-xl border border-zinc-100">
                  <p>• Før du kan lede andre, må du kunne lede dig selv</p>
                  <p>• Coachende &amp; motiverende kommunikation</p>
                  <p>• Skab balance i teamet og bevar roen</p>
                </div>
              </div>
              <div className="pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>1:1 eller løbende sparring</span>
                <Link to="/kontakt" className="text-emerald-800 font-bold hover:underline">Hør mere</Link>
              </div>
            </div>
          </div>

          {/* Det behøver ikke starte stort */}
          <div className="bg-emerald-950 text-white rounded-3xl p-8 sm:p-12 lg:p-14 text-center max-w-4xl mx-auto shadow-xl">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              Det behøver ikke starte stort.
            </h3>
            <p className="text-emerald-100 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              Et samarbejde kan begynde med ét foredrag, en workshop eller nogle få medarbejdersamtaler. Herefter kan indsatsen tilpasses det, virksomheden faktisk har brug for.
            </p>
            <div className="inline-block p-4 bg-emerald-900/80 rounded-2xl border border-emerald-700/60 mb-8">
              <p className="text-sm uppercase tracking-widest font-extrabold text-emerald-300">
                FORSTÅ FØRST. FORANDR DEREFTER.
              </p>
              <p className="text-xs text-emerald-200 mt-1">
                Biologi → Mindset → Strategi
              </p>
            </div>
            <div>
              <Link
                to="/kontakt"
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-8 py-4 rounded-xl transition-all shadow-md inline-flex items-center gap-2"
              >
                Book et uforpligtende intromøde
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRISER TIL VIRKSOMHEDER (Fra priser-v2.pdf) */}
      <section id="priser" className="py-24 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 px-3 py-1 rounded-full inline-block mb-3">
              Erhvervsaftaler
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-3">
              PRISER TIL VIRKSOMHEDER
            </h2>
            <p className="text-lg sm:text-xl font-medium text-emerald-950 mb-6 leading-relaxed">
              Foredrag, workshops og forløb, der giver medarbejdere og ledere en fælles forståelse af trivsel, belastning, relationer og adfærd – og konkrete strategier, der kan bruges i arbejdsdagen.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="/_assets/priser-v2.pdf"
                download
                className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-all shadow-sm"
              >
                <Download className="w-4 h-4" />
                Hent prisoversigt (PDF)
              </a>
              <button
                type="button"
                onClick={() => setActivePdfModal({
                  url: '/_assets/priser-v2.pdf',
                  title: 'Priser til virksomheder – TRIVSEL I FÆLLESSKABET',
                  description: 'Foredrag, workshops og forløb'
                })}
                className="inline-flex items-center gap-2 bg-white hover:bg-zinc-100 text-zinc-800 border border-zinc-300 font-semibold text-sm px-4 py-2.5 rounded-xl transition-all shadow-sm"
              >
                <Eye className="w-4 h-4" />
                Læs PDF i browser
              </button>
            </div>
          </div>

          {/* Formater: Foredrag, Workshop, Temadag */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* FOREDRAG */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 flex flex-col justify-between shadow-sm hover:border-emerald-300 transition-colors">
              <div>
                <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 block mb-2">Fælles viden</span>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">FOREDRAG</h3>
                <div className="mb-4">
                  <span className="text-4xl font-black text-zinc-900">9.500 kr.</span>
                  <span className="text-xs font-semibold text-slate-500 ml-2">ekskl. moms</span>
                </div>
                <div className="space-y-2 text-sm text-slate-700 leading-relaxed mb-6">
                  <p className="font-semibold text-zinc-900">60-90 min.</p>
                  <p>En fælles introduktion til stress, biologi og trivsel med udgangspunkt i Biologi → Mindset → Strategi.</p>
                  <p className="text-slate-600 text-xs pt-2">Velegnet til fx personaledag, temamøde eller fælles opstart.</p>
                </div>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-3.5 px-4 rounded-xl transition-colors text-sm"
              >
                Book foredrag
              </Link>
            </div>

            {/* WORKSHOP */}
            <div className="bg-emerald-950 text-white border border-emerald-800 rounded-3xl p-8 flex flex-col justify-between shadow-xl relative">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-emerald-950 text-xs font-bold uppercase tracking-wider px-3.5 py-1 rounded-full">
                Mest populære
              </div>
              <div>
                <span className="text-xs uppercase tracking-widest font-bold text-emerald-300 block mb-2">Involvering</span>
                <h3 className="text-2xl font-bold text-white mb-3">WORKSHOP</h3>
                <div className="mb-4">
                  <span className="text-4xl font-black text-emerald-400">14.500 kr.</span>
                  <span className="text-xs font-semibold text-emerald-300 ml-2">ekskl. moms</span>
                </div>
                <div className="space-y-2 text-sm text-emerald-100 leading-relaxed mb-6">
                  <p className="font-semibold text-white">Ca. 3 timer</p>
                  <p>Oplæg kombineret med refleksion og øvelser. Fokus på kommunikation, trivsel og fælles strategier.</p>
                  <p className="text-emerald-300 text-xs pt-2 font-medium">Op til 25 deltagere.</p>
                </div>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold py-3.5 px-4 rounded-xl transition-colors text-sm shadow-md"
              >
                Book workshop
              </Link>
            </div>

            {/* TEMADAG */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 flex flex-col justify-between shadow-sm hover:border-emerald-300 transition-colors">
              <div>
                <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 block mb-2">Dybdegående</span>
                <h3 className="text-2xl font-bold text-zinc-900 mb-3">TEMADAG</h3>
                <div className="mb-4">
                  <span className="text-4xl font-black text-zinc-900">22.500 kr.</span>
                  <span className="text-xs font-semibold text-slate-500 ml-2">ekskl. moms</span>
                </div>
                <div className="space-y-2 text-sm text-slate-700 leading-relaxed mb-6">
                  <p className="font-semibold text-zinc-900">Ca. 6 timer</p>
                  <p>Mere tid til fordybelse og aktiv involvering. Kan bl.a. arbejde med stress, relationer, konflikter og konkrete fælles aftaler.</p>
                  <p className="text-slate-600 text-xs pt-2">Tilpasses jeres behov.</p>
                </div>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-3.5 px-4 rounded-xl transition-colors text-sm"
              >
                Book temadag
              </Link>
            </div>
          </div>

          {/* INDIVIDUEL STØTTE OG LØBENDE SAMARBEJDE */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-3xl p-8 sm:p-10 mb-12 shadow-sm">
            <h3 className="text-2xl font-bold text-zinc-900 mb-6">
              INDIVIDUEL STØTTE OG LØBENDE SAMARBEJDE
            </h3>
            <div className="divide-y divide-zinc-200">
              <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="font-bold text-lg text-zinc-900">1:1 medarbejdersamtale</h4>
                  <p className="text-sm text-slate-600">60 min. – forebyggelse, belastning, trivsel eller sparring</p>
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-emerald-900 flex-shrink-0">
                  1.500 kr. <span className="text-xs font-normal text-slate-500">ekskl. moms</span>
                </div>
              </div>

              <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="font-bold text-lg text-zinc-900">Stressforløb</h4>
                  <p className="text-sm text-slate-600">6 x 60 min. individuelle samtaler</p>
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-emerald-900 flex-shrink-0">
                  8.500 kr. <span className="text-xs font-normal text-slate-500">ekskl. moms</span>
                </div>
              </div>

              <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="font-bold text-lg text-zinc-900">Løbende virksomhedsaftale</h4>
                  <p className="text-sm text-slate-600">Fast adgang til 1:1-samtaler og trivsels-/ledelsessparring</p>
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-emerald-900 flex-shrink-0">
                  Fra 9.500 kr./md. <span className="text-xs font-normal text-slate-500">ekskl. moms</span>
                </div>
              </div>
            </div>
          </div>

          {/* HVILKEN LØSNING PASSER TIL JER? */}
          <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-3xl p-8 sm:p-10 mb-8 max-w-4xl mx-auto">
            <h4 className="text-xl sm:text-2xl font-bold text-emerald-950 mb-4">
              HVILKEN LØSNING PASSER TIL JER?
            </h4>
            <div className="space-y-3 text-slate-700 text-sm sm:text-base leading-relaxed">
              <p>
                Et foredrag er en god fælles indgang. En workshop giver plads til at arbejde mere aktivt med jeres hverdag og udfordringer. En temadag eller et længere forløb giver mulighed for større fordybelse og opfølgning.
              </p>
              <p>
                Indhold og omfang tilpasses altid den enkelte virksomhed, deltagergruppen og det, I ønsker at skabe bevægelse omkring.
              </p>
            </div>
          </div>

          {/* Praktisk */}
          <div className="max-w-4xl mx-auto text-center mb-8">
            <p className="text-xs sm:text-sm text-slate-500">
              <strong className="text-slate-700">Praktisk:</strong> Alle priser er ekskl. moms. Transport kan tillægges. Større, længerevarende eller særligt tilpassede forløb aftales individuelt.
            </p>
          </div>

          {/* LAD OS FINDE DEN RIGTIGE INDGANG */}
          <div className="bg-white border-2 border-emerald-900/30 rounded-3xl p-8 sm:p-10 max-w-4xl mx-auto shadow-md text-center">
            <h4 className="text-xl sm:text-2xl font-bold text-emerald-950 mb-3 tracking-wide">
              LAD OS FINDE DEN RIGTIGE INDGANG
            </h4>
            <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-6 max-w-2xl mx-auto">
              Er I i tvivl om, hvilken løsning der passer bedst til jeres virksomhed, tager jeg gerne en kort og uforpligtende samtale om jeres behov.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-bold py-3.5 px-6 rounded-xl transition-colors text-sm shadow-sm"
              >
                Tag kontakt
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:26368306"
                className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-bold py-3.5 px-6 rounded-xl transition-colors text-sm"
              >
                <Phone className="w-4 h-4 text-emerald-800" />
                26 36 83 06
              </a>
              <a
                href="mailto:kontakt@ditlivditansvar.dk"
                className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-bold py-3.5 px-6 rounded-xl transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-emerald-800" />
                kontakt@ditlivditansvar.dk
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Videoudtalelser fra erhvervskunder */}
      <section id="videoudtalelser" className="py-20 bg-emerald-950/5 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/80 px-3.5 py-1.5 rounded-full inline-block mb-3">
              Kundeudtalelser på video
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Hør hvad erhvervskunderne siger
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Få et indblik i værdien af både det længerevarende samarbejde og de konkrete trivselsforløb direkte fra virksomhederne.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 max-w-5xl mx-auto">
            {/* Allan - Logistikkompagniet */}
            <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="relative aspect-video bg-zinc-950 overflow-hidden">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/allan-deltager-til-moeder.mov"
                >
                  <source src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/allan-deltager-til-moeder.mov" type="video/quicktime" />
                  <source src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/allan-deltager-til-moeder.mov" type="video/mp4" />
                  Din browser understøtter ikke video-tagget.
                </video>
              </div>
              <div className="p-6 sm:p-8 flex flex-col flex-grow justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/70 px-3 py-1 rounded-full">
                      Logistikkompagniet
                    </span>
                    <span className="text-xs text-slate-500 font-medium">Længerevarende samarbejde</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3 leading-snug">
                    "Værdien af det længerevarende samarbejde"
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed italic mb-6">
                    Allan fortæller om værdien af et tæt og løbende samarbejde med Michael, og hvordan hans aktive deltagelse til møder og kontinuerlige sparring styrker trivslen og arbejdsfællesskabet i hverdagen.
                  </p>
                </div>
                <div className="pt-5 border-t border-zinc-100 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-zinc-900 text-base">Allan</h4>
                    <p className="text-sm text-emerald-700 font-medium">Logistikkompagniet</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bahadir Demirhan - Nybolig Odense */}
            <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="relative aspect-video bg-zinc-950 overflow-hidden">
                <video 
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/459b022c-0129-44c7-b9de-c19d36d80dc6.mp4"
                >
                  <source src="https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/459b022c-0129-44c7-b9de-c19d36d80dc6.mp4" type="video/mp4" />
                  Din browser understøtter ikke video-tagget.
                </video>
              </div>
              <div className="p-6 sm:p-8 flex flex-col flex-grow justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/70 px-3 py-1 rounded-full">
                      Nybolig Odense
                    </span>
                    <span className="text-xs text-slate-500 font-medium">Teamforløb &amp; redskaber</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3 leading-snug">
                    "Blot efter få timer var vi klædt på med nye redskaber"
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed italic mb-6">
                    Nybolig Odense fik hjælp af Michael til at styrke empati, relationer og kundeforståelse. Efter blot få timer var hele teamet klædt på med konkrete værktøjer, der med det samme gjorde en forskel i hverdagen.
                  </p>
                </div>
                <div className="pt-5 border-t border-zinc-100 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-zinc-900 text-base">Bahadir Demirhan</h4>
                    <p className="text-sm text-emerald-700 font-medium">Indehaver &amp; Ejendomsmægler · Nybolig Odense</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PDF Ressourcedokumenter for Trivsel i fællesskabet */}
      <section id="materiale-pdf" className="py-24 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-full inline-block mb-3">
              Dokumenter &amp; Ressourcer
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Hent &amp; Læs Materiale om Trivsel i Fællesskabet
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Her kan du hente og gennemse materialet og priserne for virksomheder. Både til direkte visning i browseren og som download.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            {pdfDocs.map((doc) => (
              <PdfResourceCard
                key={doc.id}
                title={doc.title}
                description={doc.description}
                pdfUrl={doc.url}
                tag={doc.tag}
                highlight={doc.highlight}
                badgeText={doc.badgeText}
              />
            ))}
          </div>

          <div className="p-6 bg-white rounded-2xl border border-zinc-200 text-center max-w-2xl mx-auto shadow-sm">
            <p className="text-sm text-slate-600">
              Har I brug for et skræddersyet tilbud til jeres organisation? Kontakt Michael direkte på{' '}
              <a href="mailto:kontakt@ditlivditansvar.dk" className="text-emerald-800 font-bold underline">
                kontakt@ditlivditansvar.dk
              </a>{' '}
              eller ring på{' '}
              <a href="tel:26368306" className="text-emerald-800 font-bold underline">
                26 36 83 06
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Skal vi tage en uforpligtende snak? */}
      <section className="py-20 bg-emerald-950 text-white border-t border-emerald-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Skal vi tage en uforpligtende snak?
          </h2>
          <p className="text-emerald-100 text-lg sm:text-xl leading-relaxed mb-8 max-w-2xl mx-auto">
            Om en konkret stressramt medarbejder, forebyggelse eller trivsel i teamet.
          </p>

          <div className="inline-block p-6 sm:p-8 bg-emerald-900/80 rounded-2xl border border-emerald-800 mb-8 text-left max-w-md w-full">
            <h3 className="font-bold text-xl text-white mb-2">Michael Lunde</h3>
            <p className="text-emerald-300 text-sm mb-4">TRIVlogi® – Biologien bag trivsel</p>
            <div className="space-y-2 text-sm text-emerald-100">
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-emerald-400" />
                <a href="tel:26368306" className="hover:underline">26 36 83 06</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-emerald-400" />
                <a href="mailto:kontakt@ditlivditansvar.dk" className="hover:underline">kontakt@ditlivditansvar.dk</a>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-8 py-4 rounded-xl transition-all shadow-md text-base"
            >
              Kontakt mig nu
            </Link>
          </div>
        </div>
      </section>

      {/* Dynamic PDF Modal if active */}
      {activePdfModal && (
        <PdfModal
          isOpen={true}
          onClose={() => setActivePdfModal(null)}
          pdfUrl={activePdfModal.url}
          title={activePdfModal.title}
          description={activePdfModal.description}
        />
      )}

      {/* Image Lightbox */}
      {showImageLightbox && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowImageLightbox(false)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-3xl p-4 shadow-2xl overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowImageLightbox(false)}
              className="absolute top-6 right-6 p-2 bg-emerald-950 text-white rounded-full hover:bg-emerald-900 transition-colors z-10"
              aria-label="Luk billede"
            >
              ✕
            </button>
            <img 
              src="/_assets/mindre-stress-staerkere-faellesskaber.png" 
              alt="Mindre stress, stærkere fællesskaber - TRIVlogi model" 
              className="w-full h-auto rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TrivselIFaellesskabet;
